import { useMemo } from "react";
import LazyApexChart from '../../../client/components/LazyApexChart';
import type { ApexOptions } from "apexcharts";
import { Card, CardContent } from "../../../client/components/ui/card";
import { CardSectionHeader } from "../../../client/components/ui/card-section-header";
import { useDashboardData } from "../DashboardDataContext";
import { chartColors, pnlColor } from "../chart/chartColors";
import ChartViewToggle from "../ChartViewToggle";
import WidgetDataTable from "../WidgetDataTable";
import type { WidgetViewProps } from "../types";

function EmptyState({ label }: { label: string }) {
  return (
    <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
      {label}
    </div>
  );
}

function fmt(v: number): string {
  const abs = Math.abs(v);
  const prefix = v < 0 ? "-$" : "$";
  if (abs >= 1000) return `${prefix}${(abs / 1000).toFixed(1)}k`;
  return `${prefix}${abs.toFixed(0)}`;
}

export default function SessionDurationWidget({ viewMode = "chart", onSetViewMode }: WidgetViewProps) {
  const { analyticsData, isDark, themeId, t } = useDashboardData();

  const sessionStats = analyticsData?.sessionStats;
  const buckets = sessionStats?.durationBuckets ?? [];

  const durColors = buckets.map((b) => pnlColor(b.avgPnl));

  const tableColumns = [
    { key: "label", label: t.analytics.durationLabel, align: "left" as const },
    { key: "sessions", label: t.analytics.sessionsCountLabel, align: "right" as const },
    { key: "avgPnl", label: t.analytics.avgPnlLabel, align: "right" as const },
  ];
  const tableRows = buckets.map((b) => ({
    label: b.label,
    sessions: b.sessionCount,
    avgPnl: (
      <span className={b.avgPnl >= 0 ? "text-pnlPositive" : "text-pnlNegative"}>
        {b.avgPnl >= 0 ? "+" : ""}
        {b.avgPnl.toFixed(2)}
      </span>
    ),
  }));

  const c = chartColors();
  const gridColor = c.grid;
  const tooltipTheme = isDark ? "dark" : "light";
  const axisLabelStyle = { colors: c.axis, fontSize: "11px" };

  const durOptions: ApexOptions = useMemo(() => ({
    chart: {
      type: "bar" as const,
      toolbar: { show: false },
      background: "transparent",
      animations: { enabled: false },
    },
    plotOptions: { bar: { horizontal: false, borderRadius: 4, columnWidth: "55%", distributed: true } },
    colors: durColors,
    dataLabels: {
      enabled: true,
      formatter: fmt as any,
      // dark text reads on the emerald/red colored bars in every theme
      style: { fontSize: "11px", fontWeight: 700, colors: ["#0F1729"] },
      offsetY: -4,
    },
    grid: {
      borderColor: gridColor,
      strokeDashArray: 3,
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } },
    },
    xaxis: {
      categories: buckets.map((b) => b.label),
      labels: { style: axisLabelStyle },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: { labels: { style: axisLabelStyle, formatter: fmt as any } },
    tooltip: { theme: tooltipTheme, y: { formatter: fmt as any } },
    legend: { show: false },
    theme: { mode: isDark ? "dark" as const : "light" as const },
  }), [isDark, buckets, durColors, gridColor, tooltipTheme]);

  if (!analyticsData) {
    return (
      <Card className="h-full">
        <CardContent className="h-full p-4">
          <EmptyState label={t.analytics.noDataAvailable} />
        </CardContent>
      </Card>
    );
  }

  if (!sessionStats?.hasEnoughData || buckets.length === 0) {
    return (
      <Card className="h-full">
        <CardSectionHeader
          label={t.analytics.sessionDurationLabel}
          isDark={isDark}
        />
        <CardContent className="h-full p-4">
          <EmptyState label={t.analytics.notEnoughSessionData ?? t.analytics.noDataAvailable} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-full flex flex-col">
      <CardSectionHeader
        label={t.analytics.sessionDurationLabel}
        isDark={isDark}
        rightContent={<ChartViewToggle value={viewMode} onChange={onSetViewMode} />}
      />
      <CardContent className="flex-1 px-3 pb-2 pt-0 min-h-0">
        {viewMode === "table" ? (
          <WidgetDataTable columns={tableColumns} rows={tableRows} emptyLabel={t.analytics.noDataAvailable} />
        ) : (
          <LazyApexChart
            key={`session-dur-${themeId}`}
            options={durOptions}
            series={[{ name: t.analytics.averagePnl, data: buckets.map((b) => b.avgPnl) }]}
            type="bar"
            height="100%"
          />
        )}
      </CardContent>
    </Card>
  );
}
